import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IoCloseOutline } from 'react-icons/io5';
import {
  AMENITY_FLUSH_MODAL_PROPS,
  StyledModalBodyV2,
  StyledModalCardV2
} from '../../../../../components/modalV2';
import { Palette } from '../../../../../components/utils';
import { changeOpenedModal } from '../../../../../redux/actions/headerModal';
import { clearAmenity } from '../../../../../redux/actions/amenities';
import { GET_AMENITY_V2 } from '../../../../../redux/constants';
import { requestPending } from '../../../../../utils/status';
import LoadingAvatar from '../../../../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../../../../utils/colorCode';
import AmenityReviewDisplayV2 from './AmenityReviewDisplayV2';
import { formatAmenityBuildingsDisplay } from './amenitiesListDisplayHelpers';
import { mapBackendToFormValues } from './updateAmenityMapperV2';
import {
  UpdateAmenityAccordionStack,
  UpdateAmenityCloseButton,
  UpdateAmenityHeaderRow,
  UpdateAmenityHeaderTitle,
  UpdateAmenityStickyHeader
} from './updateAmenityV2Styles';

function AmenityDetailV2() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const days = useMemo(
    () => [
      { value: 'M', label: t('Monday') },
      { value: 'T', label: t('Tuesday') },
      { value: 'W', label: t('Wednesday') },
      { value: 'TH', label: t('Thursday') },
      { value: 'F', label: t('Friday') },
      { value: 'S', label: t('Saturday') },
      { value: 'SU', label: t('Sunday') }
    ],
    [t]
  );

  const { allBuildings } = useSelector((state) => state.building);
  const { amenity, amenityTypes, status } = useSelector((state) => state.amenities);
  const { modal, data: modalData } = useSelector((state) => state.headerModal);
  const amenityId = modalData?.amenityId;

  const isLoadingAmenity =
    modal === 'amenityDetailV2' &&
    (status === requestPending(GET_AMENITY_V2) ||
      !amenity?.id ||
      (amenityId != null && Number(amenity.id) !== Number(amenityId)));

  const reviewData = useMemo(() => {
    if (modal !== 'amenityDetailV2' || isLoadingAmenity) return null;
    const { formValues, localState } = mapBackendToFormValues(amenity, days, allBuildings);
    return {
      values: formValues,
      amenityImages: localState.amenityImages,
      waiverFile: localState.waiverFile
    };
  }, [modal, isLoadingAmenity, amenity, days, allBuildings]);

  const buildingsSummary = useMemo(
    () => formatAmenityBuildingsDisplay(amenity),
    [amenity]
  );

  const onClose = useCallback(() => {
    const returnModal = modalData?.returnModal ?? '';
    dispatch(changeOpenedModal({ modal: returnModal }));
    if (!returnModal) {
      dispatch(clearAmenity());
    }
  }, [dispatch, modalData?.returnModal]);

  const headerTitle = useMemo(() => {
    const name = (reviewData?.values?.code || '').trim();
    if (name) {
      return t('amenity_detail_header_title', {
        defaultValue: '{{name}}',
        name
      });
    }
    return t('Amenity details', { defaultValue: 'Amenity details' });
  }, [reviewData?.values?.code, t]);

  if (modal !== 'amenityDetailV2') {
    return null;
  }

  return (
    <StyledModalCardV2
      {...AMENITY_FLUSH_MODAL_PROPS}
      show={modal === 'amenityDetailV2'}
      closeOnBackdrop={false}
      onClose={onClose}
      width="555px">
      {isLoadingAmenity ? (
        <LoadingAvatar color={LOADING_BLUE} />
      ) : (
        <>
          <UpdateAmenityStickyHeader>
            <UpdateAmenityHeaderRow>
              <UpdateAmenityHeaderTitle>{headerTitle}</UpdateAmenityHeaderTitle>
              <UpdateAmenityCloseButton type="button" onClick={onClose} aria-label={t('Close')}>
                <IoCloseOutline size={16} color={Palette.CONTENT_TERTIARY} />
              </UpdateAmenityCloseButton>
            </UpdateAmenityHeaderRow>
          </UpdateAmenityStickyHeader>

          <StyledModalBodyV2 gap="0">
            <UpdateAmenityAccordionStack>
              {reviewData ? (
                <AmenityReviewDisplayV2
                  t={t}
                  values={reviewData.values}
                  amenityTypes={amenityTypes}
                  amenityImages={reviewData.amenityImages}
                  waiverFile={reviewData.waiverFile}
                  buildingsSummary={buildingsSummary}
                />
              ) : null}
            </UpdateAmenityAccordionStack>
          </StyledModalBodyV2>
        </>
      )}
    </StyledModalCardV2>
  );
}

export default AmenityDetailV2;
